import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PageHeader from '../../../shared/page-header/PageHeader';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getModulePermissions } from '../../../utils/permissionHelper';
import { logAuditEvent } from '../../../utils/auditLogger';
import { getStorageValues } from '../../../shared/CommonFunctions';
import { exportToExcel, exportToPDF } from '../../../utils/downloadExportedFiles';
import ProductGrid from './ProductGrid';
import ProductModal from './ProductModal';
import { buildProductSavePayload } from './utils/mapProduct';
import { mapProductFromApiAsync } from './utils/mapProduct';
import { validateProduct } from './productValidation';

function ProductMain(props) {
  const [productList, setProductList] = useState([]);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [userAction, setUserAction] = useState('Add');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [alert, setAlert] = useState(null);
  const [userModulePermission, setUserModulePermission] = useState({});
  const moduleName = props.Module || 'Product';

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/ec-product/getAll`);
      if (response.status === 200) {
        setProductList(response.data?.data || []);
      }
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to load products!!', 'Error', setAlert);
    }
  };

  useEffect(() => {
    const modulePermissions = getModulePermissions(props.Module);
    setUserModulePermission(modulePermissions ? modulePermissions : []);
    fetchProducts();
    logAuditEvent(moduleName, 'Accessed Module', 'Accessed Product module');
  }, [props.Module, moduleName]);

  const openAddModal = () => {
    setUserAction('Add');
    setSelectedProduct(null);
    setIsOpen(true);
    logAuditEvent(moduleName, 'Add Button Click', 'Opened add product modal');
  };

  const closeModal = () => {
    setIsOpen(false);
    setSelectedProduct(null);
    setUserAction('Add');
  };

  const onSubmit = async (e, data) => {
    if (e.target.name !== 'Add' && e.target.name !== 'Edit') {
      return { success: false };
    }

    const payload = buildProductSavePayload(data, getStorageValues('userId'));
    if (!validateProduct(payload, setAlert)) {
      return { success: false };
    }

    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-product/save`, payload);
      const result = response.data?.data;
      if (result?.success === 1) {
        const isAdd = e.target.name === 'Add';
        const productId = result.ProductId || data.ProductId;
        CreateAlert(result.message || (isAdd ? 'Product added successfully!!' : 'Product updated successfully!!'), 'Success', setAlert);
        await fetchProducts();
        if (isAdd) {
          if (productId) {
            const detailRes = await axios.get(`${getApiBaseUrl()}/ec-product/getById`, {
              params: { ProductId: productId },
            });
            const mapped = await mapProductFromApiAsync(detailRes.data?.data);
            setSelectedProduct(mapped);
            setUserAction('Edit');
          } else {
            closeModal();
          }
        } else {
          closeModal();
        }
        return { success: true, productId };
      }

      CreateAlert(result?.message || 'Failed to save product!!', 'Error', setAlert);
      return { success: false };
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to save product!!', 'Error', setAlert);
      return { success: false };
    }
  };

  const handleExportExcel = () => {
    const columns = [
      { name: 'Product Name', selector: 'ProductName' },
      { name: 'Category', selector: 'CategoryName' },
      { name: 'Sub Category', selector: 'SubCategoryName' },
      { name: 'Price', selector: 'Price' },
      { name: 'Active', selector: 'IsActive' },
    ];
    exportToExcel('Products', columns, productList);
  };

  const handleExportPDF = () => {
    const columns = [
      { name: 'Product Name', selector: 'ProductName' },
      { name: 'Category', selector: 'CategoryName' },
      { name: 'Sub Category', selector: 'SubCategoryName' },
      { name: 'Price', selector: 'Price' },
      { name: 'Active', selector: 'IsActive' },
    ];
    exportToPDF('Products', columns, productList);
  };

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={props.Module}
        onModelOpen={openAddModal}
        onRefresh={fetchProducts}
        showPDF={true}
        showExcel={true}
        showPrint={false}
        showRefresh={true}
        onExcelExport={handleExportExcel}
        onPdfExport={handleExportPDF}
        showAddButton={userModulePermission.AddModule === 1}
      />
      <ProductGrid
        ProductData={productList}
        fetchProducts={fetchProducts}
        UserPermissions={userModulePermission}
        moduleName={moduleName}
      />
      <ProductModal
        show={modalIsOpen}
        UserAction={userAction}
        selectedRow={selectedProduct}
        onClose={closeModal}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default ProductMain;
